import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { supabaseClient } from "../utils/config";
import { useBuilder } from "../builder/store/useBuilder";
import { renderHtml } from "../builder/render/renderHtml";
import { blockRegistry } from "../builder/registry/blocks";
import Palette from "../builder/components/Palette";
import Canvas from "../builder/components/Canvas";
import Inspector from "../builder/components/Inspector";
import PreviewModal from "../builder/components/PreviewModal";

const BRAND = "#069d6e";

export default function TapBuilder() {
  const navigate = useNavigate();
  const builder = useBuilder();
  const [templateName, setTemplateName] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [dragType, setDragType] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = (event) => {
    const data = event.active.data.current;
    if (data?.source === "palette") setDragType(data.blockType);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDragType(null);
    if (!over) return;

    const fromPalette = active.data.current?.source === "palette";
    if (fromPalette) {
      const blockType = active.data.current.blockType;
      const overId = over.id === "canvas-root" ? null : over.id;
      builder.addBlockAt(blockType, overId);
      return;
    }

    if (active.id !== over.id && over.id !== "canvas-root") {
      builder.reorderBlock(active.id, over.id);
    }
  };

  const buildHtml = () => renderHtml(builder.doc);

  const saveTemplate = async (thenSend) => {
    if (!templateName.trim()) {
      setToast({ type: "error", msg: "Please enter a template name." });
      return;
    }
    if (builder.doc.blocks.length === 0) {
      setToast({ type: "error", msg: "Add at least one block first." });
      return;
    }
    setSaving(true);
    const html = buildHtml();
    const { error } = await supabaseClient.from("email_template").insert({
      html,
      email_json: builder.doc,
      template_type: "custom",
      template_name: templateName.trim(),
    });
    setSaving(false);
    if (error) {
      console.error("Save error:", error);
      setToast({ type: "error", msg: "Failed to save template." });
      return;
    }
    if (thenSend) {
      navigate("/select-recepient", {
        state: { html, templateName: templateName.trim() },
      });
      return;
    }
    setToast({ type: "success", msg: "Template saved." });
  };

  const handleReset = () => {
    if (window.confirm("Clear the canvas and start over?")) {
      builder.reset();
      builder.clearStorage();
      setTemplateName("");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#eef1f4]">
      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => setToast(null)}
      >
        {toast ? (
          <Alert
            severity={toast.type}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ width: "100%" }}
          >
            {toast.msg}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>

      <header className="flex items-center justify-between gap-3 bg-white px-4 py-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <IconButton onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>
          <span className="font-semibold text-gray-800">Tap Builder</span>
        </div>

        <div className="flex items-center gap-2">
          <Tooltip title="Undo">
            <span>
              <IconButton
                size="small"
                disabled={!builder.canUndo}
                onClick={builder.undo}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo">
            <span>
              <IconButton
                size="small"
                disabled={!builder.canRedo}
                onClick={builder.redo}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Clear">
            <IconButton size="small" onClick={handleReset}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <div className="flex items-center gap-2">
          <TextField
            size="small"
            placeholder="Template name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            sx={{ width: 200 }}
          />
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewOpen(true)}
            sx={{ borderColor: BRAND, color: BRAND, "&:hover": { borderColor: BRAND } }}
          >
            Preview
          </Button>
          <Button
            variant="outlined"
            disabled={saving}
            onClick={() => saveTemplate(false)}
            sx={{ borderColor: BRAND, color: BRAND, "&:hover": { borderColor: BRAND } }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => saveTemplate(true)}
            sx={{
              backgroundColor: BRAND,
              "&:hover": { backgroundColor: BRAND },
              "&.Mui-disabled": { backgroundColor: "#9ccabb", color: "#fff" },
            }}
          >
            {saving ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Save & Continue"
            )}
          </Button>
        </div>
      </header>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setDragType(null)}
      >
        <div className="flex flex-1 min-h-0">
          <aside className="w-56 shrink-0 border-r border-gray-200 bg-gray-50">
            <Palette onAdd={builder.addBlock} />
          </aside>

          <main className="flex-1 min-w-0 overflow-hidden">
            <Canvas
              doc={builder.doc}
              selectedId={builder.selectedId}
              onSelect={builder.select}
              onDuplicate={builder.duplicateBlock}
              onRemove={builder.removeBlock}
            />
          </main>

          <aside className="w-80 shrink-0 border-l border-gray-200 bg-white">
            <Inspector
              block={builder.selectedBlock}
              settings={builder.doc.settings}
              onUpdateBlock={builder.updateBlock}
              onUpdateSettings={builder.updateSettings}
            />
          </aside>
        </div>

        <DragOverlay>
          {dragType ? (
            <div className="rounded-lg border border-[#069d6e] bg-white px-4 py-2 text-sm text-[#069d6e] shadow-md">
              {blockRegistry[dragType]?.label}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <PreviewModal
        open={previewOpen}
        html={buildHtml()}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}
